import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, Trash2, Utensils, Copy, Sparkles, CheckSquare, Square } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

export default function MealPlansPage() {
    const [plans, setPlans] = useState([]);
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const nav = useNavigate();
    const confirm = useConfirm();

    const load = async () => {
        const { data } = await api.get("/meal-plans");
        setPlans(data);
    };
    useEffect(() => { load(); }, []);

    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === plans.length) setSelected(new Set());
        else setSelected(new Set(plans.map((p) => p.id)));
    };
    const cancelSelect = () => { setSelectMode(false); setSelected(new Set()); };

    const bulkDelete = async () => {
        if (selected.size === 0) return;
        const ok = await confirm({
            title: `Delete ${selected.size} meal plan${selected.size > 1 ? "s" : ""}?`,
            message: "Active assignments for these plans will also be removed.",
            confirmLabel: `Delete ${selected.size}`,
        });
        if (!ok) return;
        await api.post("/meal-plans/bulk-delete", { ids: Array.from(selected) });
        setPlans(plans.filter((p) => !selected.has(p.id)));
        cancelSelect();
    };

    const createNew = async (withAi = false) => {
        const { data } = await api.post("/meal-plans", {
            name: withAi ? "AI Meal Plan" : "Untitled Meal Plan",
            description: "",
            daily_calories: 2000, protein_g: 150, carbs_g: 200, fat_g: 70,
            meals: withAi ? [] : [
                { name: "Meal 1", items: [], notes: "" },
                { name: "Meal 2", items: [], notes: "" },
                { name: "Meal 3", items: [], notes: "" },
            ],
        });
        nav(`/meal-plans/${data.id}${withAi ? "?ai=1" : ""}`);
    };

    const duplicate = async (id) => {
        const { data } = await api.post(`/meal-plans/${id}/duplicate`);
        nav(`/meal-plans/${data.id}`);
    };

    const remove = async (id, name) => {
        const ok = await confirm({ title: "Delete meal plan?", message: `"${name}" will be removed. This can't be undone.`, confirmLabel: "Delete plan" });
        if (!ok) return;
        await api.delete(`/meal-plans/${id}`);
        setPlans(plans.filter((p) => p.id !== id));
    };

    return (
        <div className="p-8 space-y-6" data-testid="meal-plans-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="label-tiny mb-2">/ Nutrition</p>
                    <h1 className="font-serif text-6xl">Meal Plans</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {selectMode ? (
                        <>
                            <button onClick={toggleAll} className="btn-ghost inline-flex items-center gap-2" data-testid="mp-select-all">
                                {selected.size === plans.length && plans.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
                                {selected.size}/{plans.length}
                            </button>
                            <button onClick={bulkDelete} disabled={selected.size === 0} className="btn-primary bg-[var(--danger)] hover:bg-[var(--danger)] inline-flex items-center gap-2" data-testid="mp-bulk-delete">
                                <Trash2 size={14} /> Delete selected
                            </button>
                            <button onClick={cancelSelect} className="btn-ghost" data-testid="mp-cancel-select">Cancel</button>
                        </>
                    ) : (
                        <>
                            {plans.length > 0 && (
                                <button onClick={() => setSelectMode(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="mp-select-mode">
                                    <CheckSquare size={14} /> Select
                                </button>
                            )}
                            <button onClick={() => createNew(true)} className="btn-ghost inline-flex items-center gap-2 border-[var(--gold-deep)]" data-testid="ai-new-meal-plan-btn">
                                <Sparkles size={14} className="text-[var(--gold-bright)]" /> Generate with AI
                            </button>
                            <button onClick={() => createNew(false)} className="btn-primary inline-flex items-center gap-2" data-testid="new-meal-plan-btn">
                                <Plus size={14} /> New meal plan
                            </button>
                        </>
                    )}
                </div>
            </div>

            {plans.length === 0 ? (
                <div className="border border-dashed border-[var(--border-bright)] bg-[var(--surface)] p-12 text-center">
                    <Utensils size={32} className="text-[var(--gold)] mx-auto mb-4" />
                    <p className="font-serif text-3xl mb-2">No meal plans yet</p>
                    <p className="text-sm text-[var(--text-muted)] mb-5">Build one template, assign to any athlete with one click.</p>
                    <button onClick={createNew} className="btn-primary">Create your first →</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
                    {plans.map((p) => (
                        <div key={p.id} className={`bg-[var(--surface)] p-6 transition-colors ${selectMode && selected.has(p.id) ? "ring-2 ring-[var(--gold)]" : "hover:bg-[var(--surface-hover)]"}`} data-testid={`meal-plan-card-${p.id}`}>
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-start gap-3 flex-1">
                                    {selectMode && (
                                        <button onClick={() => toggleOne(p.id)} className="mt-1 text-[var(--gold)]" data-testid={`mp-select-${p.id}`}>
                                            {selected.has(p.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                                        </button>
                                    )}
                                    <Link to={`/meal-plans/${p.id}`} className="font-serif text-2xl hover:text-[var(--gold)]">{p.name}</Link>
                                </div>
                                {!selectMode && (
                                    <div className="flex gap-1">
                                        <button onClick={() => duplicate(p.id)} className="text-[var(--text-muted)] hover:text-[var(--gold)]" title="Duplicate" data-testid={`mp-duplicate-${p.id}`}><Copy size={14} /></button>
                                        <button onClick={() => remove(p.id, p.name)} className="text-[var(--text-muted)] hover:text-[var(--danger)]" title="Delete" data-testid={`mp-delete-${p.id}`}><Trash2 size={14} /></button>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-4 gap-2 mb-4">
                                <div><p className="label-tiny">KCAL</p><p className="font-display text-xl">{p.daily_calories}</p></div>
                                <div><p className="label-tiny">P</p><p className="font-display text-xl text-[var(--gold-bright)]">{p.protein_g}g</p></div>
                                <div><p className="label-tiny">C</p><p className="font-display text-xl">{p.carbs_g}g</p></div>
                                <div><p className="label-tiny">F</p><p className="font-display text-xl">{p.fat_g}g</p></div>
                            </div>
                            <p className="text-xs text-[var(--text-muted)] font-mono mb-3">{(p.meals || []).length} meals</p>
                            <Link to={`/meal-plans/${p.id}`} className="btn-ghost text-xs w-full block text-center">Edit / Assign →</Link>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
